/* eslint-disable consistent-return, react-hooks/exhaustive-deps */
import { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, CircularProgress, Button, IconButton, Tooltip, Snackbar, TextField, Alert } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { alpha } from '@mui/material/styles';
import Iconify from '../Iconify';
import { callApi } from '../../utils/apiCall';

const POLL_INTERVAL_MS = 5000;
const MAX_POLLS = 120; // stop after 10 minutes

const EMAIL_STEPS = [
  { key: 'sent', label: 'Payment link sent via email', icon: 'mdi:email-fast-outline' },
  { key: 'delivered', label: 'Email delivered', icon: 'mdi:email-check-outline' },
  { key: 'opened', label: 'Email opened', icon: 'mdi:email-open-outline' },
  { key: 'paid', label: 'Payment completed', icon: 'mdi:check-decagram' },
];

const WHATSAPP_STEPS = [
  { key: 'sent', label: 'Payment link sent via WhatsApp', icon: 'mdi:whatsapp' },
  { key: 'paid', label: 'Payment completed', icon: 'mdi:check-decagram' },
];

const BOTH_STEPS = [
  { key: 'sent', label: 'Payment link sent via email', icon: 'mdi:email-fast-outline' },
  { key: 'whatsapp_sent', label: 'Payment link sent via WhatsApp', icon: 'mdi:whatsapp' },
  { key: 'delivered', label: 'Email delivered', icon: 'mdi:email-check-outline' },
  { key: 'opened', label: 'Email opened', icon: 'mdi:email-open-outline' },
  { key: 'paid', label: 'Payment completed', icon: 'mdi:check-decagram' },
];

const getTrackingSteps = (channel) => {
  if (channel === 'whatsapp') return WHATSAPP_STEPS;
  if (channel === 'both') return BOTH_STEPS;
  return EMAIL_STEPS;
};

/**
 * Real-time payment link tracker — polls checkout session for payment status
 * and shows contact info + WhatsApp send option.
 */
export default function PaymentLinkTracker({
  practiceId, leadId, treatmentId, paymentId,
  contactEmail, contactPhone, checkoutUrl, linkChannel,
  onPaymentStatusChange, externalPaid,
  onMarkAsPaid, markingPaid,
}) {
  const [paymentStatus, setPaymentStatus] = useState('unpaid'); // 'unpaid' | 'paid'
  const [emailEvents, setEmailEvents] = useState([]); // e.g. ['delivered', 'opened']
  const [polling, setPolling] = useState(true);
  const [copied, setCopied] = useState(false);
  const [whatsappSent, setWhatsappSent] = useState(false);
  const [pollError, setPollError] = useState(null);
  // Resend / edit email state
  const [editingEmail, setEditingEmail] = useState(false);
  const [editEmail, setEditEmail] = useState(contactEmail || '');
  const [resending, setResending] = useState(false);
  const [resendResult, setResendResult] = useState(null); // 'success' | 'error'
  const [resendError, setResendError] = useState(null);
  const pollCount = useRef(0);
  const consecutiveErrors = useRef(0);
  const mountedRef = useRef(true);
  const intervalRef = useRef(null);

  const checkStatus = useCallback(async () => {
    if (!practiceId || !leadId || !treatmentId || !paymentId) return;
    if (!mountedRef.current) return;

    try {
      const res = await callApi(
        `/api/v1/practices/${practiceId}/leads/${leadId}/treatments/${treatmentId}/payments/${paymentId}/checkout_sessions/`
      );
      if (!mountedRef.current) return;

      if (res.ok && Array.isArray(res.data?.results) && res.data.results.length > 0) {
        consecutiveErrors.current = 0;
        const session = res.data.results[0];

        // Parse Mailgun email events from the API response
        if (Array.isArray(session.email_events) && session.email_events.length > 0) {
          setEmailEvents(session.email_events.map((e) => e.event || e));
        }

        if (session.payment_status === 'paid') {
          setPaymentStatus('paid');
          setPolling(false);
          if (intervalRef.current) clearInterval(intervalRef.current);
          if (onPaymentStatusChange) onPaymentStatusChange('paid');
          return;
        }
      } else if (!res.ok) {
        // HTTP error (404, 500, etc.) — callApi doesn't throw on these
        consecutiveErrors.current += 1;
        if (consecutiveErrors.current >= 3) {
          setPollError('Unable to check payment status. Please check the payment tab for updates.');
          setPolling(false);
          if (intervalRef.current) clearInterval(intervalRef.current);
          return;
        }
      }
    } catch (err) {
      if (!mountedRef.current) return;
      console.error('[PaymentLinkTracker] Poll failed', err);
      consecutiveErrors.current += 1;
      if (consecutiveErrors.current >= 3) {
        setPollError('Unable to check payment status. Please check the payment tab for updates.');
        setPolling(false);
        if (intervalRef.current) clearInterval(intervalRef.current);
        return;
      }
    }

    pollCount.current += 1;
    if (pollCount.current >= MAX_POLLS) {
      setPolling(false);
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
  }, [practiceId, leadId, treatmentId, paymentId]);

  // React to parent marking payment as paid (e.g. "Mark as Paid" button in footer)
  useEffect(() => {
    if (externalPaid && paymentStatus !== 'paid') {
      setPaymentStatus('paid');
      setPolling(false);
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
  }, [externalPaid]);

  useEffect(() => {
    mountedRef.current = true;
    if (!practiceId || !leadId || !treatmentId || !paymentId) {
      setPolling(false);
      return;
    }

    const timeout = setTimeout(() => {
      checkStatus();
      intervalRef.current = setInterval(checkStatus, POLL_INTERVAL_MS);
    }, 2000);

    return () => {
      mountedRef.current = false;
      clearTimeout(timeout);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [practiceId, leadId, treatmentId, paymentId, checkStatus]);

  // ── Step state logic ───────────────────────────────────────────────
  const channel = linkChannel || 'email';
  const trackingSteps = getTrackingSteps(channel);

  const getStepState = (stepKey) => {
    if (stepKey === 'sent') return 'done';
    if (stepKey === 'whatsapp_sent') return 'done';
    if (stepKey === 'delivered') {
      if (emailEvents.includes('delivered') || paymentStatus === 'paid') return 'done';
      return pollCount.current >= 1 ? 'active' : 'pending';
    }
    if (stepKey === 'opened') {
      if (emailEvents.includes('opened') || emailEvents.includes('clicked') || paymentStatus === 'paid') return 'done';
      if (emailEvents.includes('delivered')) return 'active';
      return 'pending';
    }
    if (stepKey === 'paid') {
      if (paymentStatus === 'paid') return 'done';
      // For WhatsApp-only, mark active immediately (no email delivery to wait for)
      if (channel === 'whatsapp') return 'active';
      if (emailEvents.includes('delivered')) return 'active';
      return 'pending';
    }
    return 'pending';
  };

  const getStepColor = (state) => {
    if (state === 'done') return '#00AB55';
    if (state === 'active') return '#FFAB00';
    return 'rgba(145, 158, 171, 0.32)';
  };

  const getStepIcon = (step, state) => {
    if (state === 'done') return 'mdi:check-circle';
    return step.icon;
  };

  const isPaid = paymentStatus === 'paid';
  const headerIcon = channel === 'whatsapp' ? 'mdi:whatsapp' : 'mdi:email-sync-outline';

  // ── Actions ────────────────────────────────────────────────────────
  const handleCopyLink = () => {
    if (!checkoutUrl) return;
    if (!navigator.clipboard?.writeText) {
      console.warn('[PaymentLinkTracker] Clipboard API not available');
      return;
    }
    navigator.clipboard.writeText(checkoutUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch((err) => {
      console.warn('[PaymentLinkTracker] Clipboard write failed', err);
    });
  };

  const handleSendWhatsApp = () => {
    if (!checkoutUrl || !contactPhone) return;
    // Clean the phone number — strip spaces and leading 0, add 44 for UK
    let phone = (contactPhone || '').replace(/[\s-]/g, '');
    if (phone.startsWith('0')) phone = `44${phone.slice(1)}`;
    if (!phone.startsWith('+')) phone = `+${phone}`;

    const message = encodeURIComponent(
      `Here is your secure payment link:\n${checkoutUrl}\n\nPlease complete the payment at your convenience.`
    );
    const waWindow = window.open(`https://wa.me/${phone.replace('+', '')}?text=${message}`, '_blank');
    if (waWindow) {
      setWhatsappSent(true);
    } else {
      console.warn('[PaymentLinkTracker] WhatsApp popup was blocked');
    }
  };

  const handleResendEmail = async () => {
    if (!practiceId || !leadId || !treatmentId || !paymentId) return;
    setResending(true);
    setResendResult(null);
    setResendError(null);

    try {
      // If the email was changed, update the lead contact first
      const emailChanged = editEmail.trim() && editEmail.trim() !== contactEmail;
      if (emailChanged) {
        const patchRes = await callApi(
          `/api/v1/practices/${practiceId}/leads/${leadId}/`,
          'patch',
          { lead_contact: { email: editEmail.trim() } },
        );
        if (!patchRes.ok) {
          setResendError('Failed to update email address. The link will be sent to the original email.');
        }
      }

      // Create a new checkout session — backend sends a fresh payment link email
      const res = await callApi(
        `/api/v1/practices/${practiceId}/leads/${leadId}/treatments/${treatmentId}/payments/${paymentId}/checkout_sessions/`,
        'post',
        {},
      );
      if (res.ok) {
        setResendResult('success');
        setEditingEmail(false);
      } else {
        setResendResult('error');
        setResendError('Failed to resend payment link. Try copying the link manually.');
      }
    } catch (err) {
      console.error('[PaymentLinkTracker] Resend failed', err);
      setResendResult('error');
      setResendError('An unexpected error occurred while resending.');
    } finally {
      setResending(false);
    }
  };

  return (
    <Box sx={{
      mt: 2,
      p: 2,
      borderRadius: 2,
      bgcolor: isPaid ? alpha('#00AB55', 0.06) : alpha('#FFAB00', 0.06),
      border: '1px solid',
      borderColor: isPaid ? alpha('#00AB55', 0.16) : alpha('#FFAB00', 0.16),
      transition: 'all 0.3s ease',
      textAlign: 'left',
    }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
        <Iconify
          icon={isPaid ? 'mdi:check-decagram' : headerIcon}
          width={18}
          sx={{ color: isPaid ? '#00AB55' : '#FFAB00', mr: 1 }}
        />
        <Typography variant="subtitle2" sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>
          {isPaid ? 'Payment received' : 'Payment link tracking'}
        </Typography>
        {polling && !isPaid && (
          <CircularProgress size={14} sx={{ ml: 'auto', color: '#FFAB00' }} />
        )}
        {isPaid && (
          <Iconify icon="mdi:check-bold" width={16} sx={{ ml: 'auto', color: '#00AB55' }} />
        )}
      </Box>

      {/* Contact info bar — email is editable when not paid */}
      {(contactEmail || contactPhone) && (
        <Box sx={{
          mb: 1.5,
          p: 1.5,
          borderRadius: '10px',
          bgcolor: 'rgba(255,255,255,0.6)',
          border: '1px solid rgba(145, 158, 171, 0.12)',
        }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
            {contactEmail && !editingEmail && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Iconify icon="mdi:email-outline" width={15} sx={{ color: 'text.disabled' }} />
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                  {editEmail.trim() && editEmail.trim() !== contactEmail ? editEmail.trim() : contactEmail}
                </Typography>
                {!isPaid && (
                  <Tooltip title="Edit email address">
                    <IconButton size="small" onClick={() => setEditingEmail(true)} sx={{ p: 0.25, ml: 0.25 }}>
                      <Iconify icon="mdi:pencil-outline" width={14} sx={{ color: 'text.disabled' }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            )}
            {editingEmail && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>
                <TextField
                  size="small"
                  type="email"
                  value={editEmail}
                  onChange={(e) => setEditEmail(e.target.value)}
                  placeholder="Patient email"
                  InputProps={{
                    startAdornment: <Iconify icon="mdi:email-outline" width={15} sx={{ color: 'text.disabled', mr: 0.5 }} />,
                    sx: { fontSize: '0.75rem', height: 32 },
                  }}
                  sx={{ flex: 1, minWidth: 160 }}
                />
                <Tooltip title="Save">
                  <IconButton size="small" onClick={() => setEditingEmail(false)} sx={{ p: 0.25 }}>
                    <Iconify icon="mdi:check" width={16} sx={{ color: '#00AB55' }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Cancel">
                  <IconButton size="small" onClick={() => { setEditEmail(contactEmail || ''); setEditingEmail(false); }} sx={{ p: 0.25 }}>
                    <Iconify icon="mdi:close" width={16} sx={{ color: 'text.disabled' }} />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            {contactPhone && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Iconify icon="mdi:phone-outline" width={15} sx={{ color: 'text.disabled' }} />
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                  {contactPhone}
                </Typography>
              </Box>
            )}
          </Box>
          {editEmail.trim() && editEmail.trim() !== contactEmail && !editingEmail && (
            <Typography variant="caption" sx={{ color: '#078DEE', fontSize: '0.7rem', display: 'block', mt: 0.5 }}>
              Email updated — click Resend to send to the new address
            </Typography>
          )}
        </Box>
      )}

      {/* Step indicators */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, px: 0.5 }}>
        {trackingSteps.map((step, idx) => {
          const state = getStepState(step.key);
          const color = getStepColor(state);
          return (
            <Box key={step.key} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Box sx={{ width: 24, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {idx > 0 && (
                  <Box sx={{
                    width: '2px',
                    height: '6px',
                    bgcolor: getStepState(trackingSteps[idx - 1].key) === 'done' ? '#00AB55' : 'rgba(145, 158, 171, 0.24)',
                    mb: '2px',
                    borderRadius: 1,
                    transition: 'background-color 0.3s ease',
                  }} />
                )}
                <Iconify
                  icon={getStepIcon(step, state)}
                  width={18}
                  sx={{
                    color,
                    transition: 'color 0.3s ease',
                    ...(state === 'active' ? {
                      animation: 'pulse 2s ease-in-out infinite',
                      '@keyframes pulse': {
                        '0%, 100%': { opacity: 1 },
                        '50%': { opacity: 0.5 },
                      },
                    } : {}),
                  }}
                />
              </Box>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.8125rem',
                  color: state === 'pending' ? 'text.disabled' : 'text.primary',
                  fontWeight: state === 'active' ? 600 : 400,
                  transition: 'all 0.3s ease',
                }}
              >
                {step.label}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Resend feedback */}
      {resendResult === 'success' && (
        <Alert severity="success" sx={{ mt: 1.5, py: 0.25, fontSize: '0.75rem' }} onClose={() => setResendResult(null)}>
          Payment link resent successfully
        </Alert>
      )}
      {resendResult === 'error' && resendError && (
        <Alert severity="warning" sx={{ mt: 1.5, py: 0.25, fontSize: '0.75rem' }} onClose={() => { setResendResult(null); setResendError(null); }}>
          {resendError}
        </Alert>
      )}

      {/* Actions: Resend Email, WhatsApp, Copy link */}
      {!isPaid && (
        <Box sx={{
          display: 'flex',
          gap: 1,
          mt: 2,
          pt: 1.5,
          borderTop: '1px solid rgba(145, 158, 171, 0.12)',
          flexWrap: 'wrap',
          alignItems: 'center',
        }}>
          {/* Resend email button — visible when email channel was used */}
          {channel !== 'whatsapp' && (
            <Button
              size="small"
              variant="outlined"
              onClick={handleResendEmail}
              disabled={resending}
              startIcon={resending
                ? <CircularProgress size={14} />
                : <Iconify icon="mdi:email-arrow-right-outline" width={16} />}
              sx={{
                textTransform: 'none',
                fontSize: '0.75rem',
                fontWeight: 500,
                borderColor: alpha('#078DEE', 0.4),
                color: '#078DEE',
                py: 0.25,
                px: 1.5,
                borderRadius: '20px',
                '&:hover': {
                  bgcolor: alpha('#078DEE', 0.08),
                  borderColor: '#078DEE',
                },
              }}
            >
              {resending ? 'Sending…' : 'Resend Email'}
            </Button>
          )}
          {/* Show "Also send via WhatsApp" only if email was the primary channel */}
          {channel !== 'whatsapp' && channel !== 'both' && contactPhone && checkoutUrl && (
            <Button
              size="small"
              variant="outlined"
              onClick={handleSendWhatsApp}
              startIcon={<Iconify icon="mdi:whatsapp" width={16} />}
              disabled={whatsappSent}
              sx={{
                textTransform: 'none',
                fontSize: '0.75rem',
                fontWeight: 500,
                borderColor: whatsappSent ? alpha('#00AB55', 0.24) : '#25D366',
                color: whatsappSent ? 'text.disabled' : '#25D366',
                py: 0.25,
                px: 1.5,
                borderRadius: '20px',
                '&:hover': {
                  bgcolor: alpha('#25D366', 0.08),
                  borderColor: '#25D366',
                },
              }}
            >
              {whatsappSent ? 'Sent' : 'WhatsApp'}
            </Button>
          )}
          {checkoutUrl && (
            <Tooltip title={copied ? 'Copied!' : 'Copy payment link'}>
              <IconButton
                size="small"
                onClick={handleCopyLink}
                sx={{ border: '1px solid rgba(145, 158, 171, 0.24)', borderRadius: '20px', p: 0.5 }}
              >
                <Iconify icon={copied ? 'mdi:check' : 'mdi:content-copy'} width={16} sx={{ color: copied ? '#00AB55' : 'text.secondary' }} />
              </IconButton>
            </Tooltip>
          )}
          {onMarkAsPaid && (
            <LoadingButton
              size="small"
              variant="outlined"
              loading={markingPaid}
              onClick={onMarkAsPaid}
              startIcon={<Iconify icon="mdi:check-circle-outline" width={16} />}
              sx={{
                ml: 'auto',
                textTransform: 'none',
                fontSize: '0.75rem',
                fontWeight: 500,
                borderColor: alpha('#00AB55', 0.4),
                color: '#00AB55',
                py: 0.25,
                px: 1.5,
                borderRadius: '20px',
                '&:hover': {
                  bgcolor: alpha('#00AB55', 0.08),
                  borderColor: '#00AB55',
                },
              }}
            >
              Mark as Paid
            </LoadingButton>
          )}
        </Box>
      )}

      {/* Timeout / error notice */}
      {!polling && !isPaid && (
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1.5 }}>
          {pollError || 'Live tracking timed out — check the lead\u0027s payment tab for the latest status.'}
        </Typography>
      )}

      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
        message="Payment link copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}

PaymentLinkTracker.propTypes = {
  practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  treatmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  paymentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  contactEmail: PropTypes.string,
  contactPhone: PropTypes.string,
  checkoutUrl: PropTypes.string,
  linkChannel: PropTypes.oneOf(['email', 'whatsapp', 'both']),
  onPaymentStatusChange: PropTypes.func,
  externalPaid: PropTypes.bool,
  onMarkAsPaid: PropTypes.func,
  markingPaid: PropTypes.bool,
};

PaymentLinkTracker.defaultProps = {
  contactEmail: null,
  contactPhone: null,
  checkoutUrl: null,
  linkChannel: 'email',
  onPaymentStatusChange: null,
  externalPaid: false,
  onMarkAsPaid: null,
  markingPaid: false,
};
